import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PS, toneAccent } from '../theme';
import { Avatar, Chip, Progress, VerifiedMark, Wordmark, DemoBadge } from '../lib/ui';
import { Icon } from '../lib/icons';
import { thb } from '../lib/format';
import MemberLayout from '../components/MemberLayout';
import ProductShareShowcase from '../components/ProductShareShowcase';
import NotificationBell from '../components/NotificationBell';
import { useAuth } from '../auth/AuthContext';
import { useFetch } from '../lib/useFetch';
import { HIGHLIGHTS } from '../data/knowledge';

const HL_TONE = {
  ink: { bg: PS.ink, fg: '#f5f1ea', tag: 'rgba(245,241,234,0.6)' },
  terra: { bg: 'oklch(0.62 0.12 38)', fg: '#fff', tag: 'rgba(255,255,255,0.72)' },
  green: { bg: 'oklch(0.42 0.07 155)', fg: '#fff', tag: 'rgba(255,255,255,0.72)' },
  danger: { bg: 'oklch(0.46 0.13 28)', fg: '#fff', tag: 'rgba(255,255,255,0.72)' },
};

function HighlightStrip({ nav }) {
  return (
    <>
      <div style={{ padding: '0 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.2 }}>รู้ไว้ก่อนเล่นแชร์</div>
        <div className="ps-press" style={{ fontSize: 12, color: PS.textMuted }} onClick={() => nav('/app/learn')} data-testid="home-learn-all">ดูคู่มือทั้งหมด</div>
      </div>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '0 16px 18px' }}>
        {HIGHLIGHTS.map((h) => {
          const t = HL_TONE[h.tone] || HL_TONE.ink;
          return (
            <div key={h.id} data-testid={`home-highlight-${h.id}`} onClick={() => nav(`/app/learn/${h.id}`)} className="ps-press ps-fade-up"
              style={{ flexShrink: 0, width: 200, background: t.bg, color: t.fg, borderRadius: 16, padding: 15, position: 'relative', overflow: 'hidden' }}>
              <div style={{ fontSize: 9.5, color: t.tag, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>{h.tag}</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 7, letterSpacing: -0.3, lineHeight: 1.25 }}>{h.title}</div>
              <div style={{ fontSize: 11, color: t.tag, marginTop: 7, lineHeight: 1.4 }}>{h.sub}</div>
              <div style={{ position: 'absolute', right: -16, top: -16, width: 60, height: 60, border: `1px solid ${t.tag}`, opacity: 0.3, transform: 'rotate(45deg)' }} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function ActiveCircleCard({ c, onClick }) {
  const accent = toneAccent(c.verified ? 'terra' : 'default');
  const pending = c.my_status === 'pending';
  return (
    <div data-testid={`home-circle-${c.circle_id}`} onClick={onClick} className="ps-press ps-fade-up"
      style={{ margin: '0 16px 10px', background: PS.surface, borderRadius: 18, padding: 16, border: `1px solid ${PS.line}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: 2, background: accent, transform: 'rotate(45deg)' }} />
            <div style={{ fontSize: 10.5, color: PS.textMuted, letterSpacing: 0.8, textTransform: 'uppercase' }}>{c.area_name}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
            {c.is_demo && <DemoBadge />}
          </div>
          <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2 }}>ท้าว: {c.host_name}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="num" style={{ fontSize: 22, fontWeight: 500, lineHeight: 1 }}>{thb(c.amount_per_round)}</div>
          <div style={{ fontSize: 10.5, color: PS.textMuted, marginTop: 2 }}>ต่องวด</div>
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: PS.textMuted, marginBottom: 5, letterSpacing: 0.3 }}>
          <span>งวด {c.current_round} / {c.rounds_count}</span>
          <span>{c.members}/{c.members_cap} คน</span>
        </div>
        <Progress value={c.current_round} max={c.rounds_count} color={accent} height={3} />
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
        {pending ? <Chip tone="warn">รอท้าวอนุมัติ</Chip> : (c.tags || []).map(t => <Chip key={t} tone="outline">{t}</Chip>)}
        {!pending && c.verified && <Chip tone="green"><VerifiedMark size={10} /> ตรวจสอบแล้ว</Chip>}
      </div>
    </div>
  );
}

// ── Guest exploration home ─────────────────────────────────────────────
function GuestHome({ nav, login }) {
  const { data: circles } = useFetch('/circles?max_km=300&verified_only=true&sort=rating');
  const top = (circles || []).slice(0, 3);
  return (
    <MemberLayout>
      <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Wordmark />
        <button data-testid="guest-home-login-btn" onClick={login} className="ps-press" style={{ background: PS.ink, color: PS.page, border: 'none', padding: '8px 16px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, fontFamily: PS.fontUI }}>เข้าสู่ระบบ</button>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: '4px 20px 16px' }}>
          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '5px 11px', borderRadius: 999, background: 'oklch(0.78 0.06 155 / 0.3)', color: 'oklch(0.34 0.08 155)', fontSize: 11, fontWeight: 600, marginBottom: 14 }}>
            <Icon.shield size={12} stroke="oklch(0.38 0.08 155)" /> ผู้เก็บหลักฐานคนกลาง · ถูกกฎหมาย
          </div>
          <div style={{ fontSize: 27, fontWeight: 700, letterSpacing: -0.8, lineHeight: 1.15 }}>เล่นแชร์อย่างมั่นใจ<br />เพราะทุกอย่างมี<span style={{ color: PS.terracotta }}>หลักฐาน</span></div>
          <div style={{ fontSize: 13.5, color: PS.textMuted, marginTop: 10, lineHeight: 1.6 }}>สำรวจวงแชร์ใกล้คุณและศึกษาวิธีเล่นได้ฟรี ไม่ต้องสมัคร — พร้อมเมื่อไหร่ค่อยเข้าร่วม</div>
        </div>

        <HighlightStrip nav={nav} />

        <ProductShareShowcase />

        <div style={{ padding: '0 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.2 }}>วงน่าเชื่อถือใกล้คุณ</div>
          <div className="ps-press" style={{ fontSize: 12, color: PS.textMuted }} onClick={() => nav('/app/discover')} data-testid="guest-see-all">ดูบนแผนที่</div>
        </div>
        {top.map((c) => (
          <div key={c.circle_id} data-testid={`guest-circle-${c.circle_id}`} onClick={() => nav(`/app/circle/${c.circle_id}`)} className="ps-press ps-fade-up" style={{ margin: '0 16px 10px', background: PS.surface, borderRadius: 18, padding: 14, border: `1px solid ${PS.line}`, display: 'flex', gap: 12, alignItems: 'center' }}>
            <Avatar name={c.host_name} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                {c.is_demo && <DemoBadge />}
              </div>
              <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2, display: 'flex', gap: 5, alignItems: 'center' }}><Icon.pin size={11} stroke={PS.textMuted} />{c.distance_km} กม. · {c.area_name}</div>
              <div style={{ display: 'flex', gap: 5, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {c.verified && <Chip tone="green"><VerifiedMark size={10} />ตรวจสอบ</Chip>}
                <Chip tone="outline">★ {c.rating}</Chip>
                <Chip tone="terra">รับอีก {c.open_slots} คน</Chip>
              </div>
            </div>
            <div className="num" style={{ fontSize: 17, fontWeight: 500 }}>{thb(c.amount_per_round)}</div>
          </div>
        ))}

        <div onClick={() => nav('/about')} className="ps-press" style={{ margin: '8px 16px 16px', padding: 16, background: PS.panel, borderRadius: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: PS.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.info size={20} stroke={PS.ink} /></div>
          <div style={{ flex: 1 }}><div style={{ fontSize: 13.5, fontWeight: 600 }}>Peershare ทำงานอย่างไร</div><div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 1 }}>3 บทบาท · เราเก็บหลักฐาน ไม่ถือเงิน</div></div>
          <Icon.chevronR size={16} stroke={PS.textDim} />
        </div>

        <div style={{ margin: '0 16px 22px', padding: 20, background: PS.ink, color: PS.page, borderRadius: 20, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>พร้อมเข้าร่วมหรือตั้งวงแล้ว?</div>
          <div style={{ fontSize: 12, color: 'rgba(245,241,234,0.6)', marginTop: 6, lineHeight: 1.5 }}>เข้าสู่ระบบด้วย Google เพื่อเข้าร่วมวง แนบหลักฐาน และดูเครดิต</div>
          <button data-testid="guest-cta-login" onClick={login} className="ps-press" style={{ marginTop: 14, height: 48, padding: '0 26px', background: PS.page, color: PS.ink, border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 700, fontFamily: PS.fontUI }}>เริ่มใช้งานด้วย Google</button>
          <div style={{ position: 'absolute', right: -20, top: -20, width: 90, height: 90, border: '1px solid rgba(245,241,234,0.08)', transform: 'rotate(45deg)' }} />
        </div>
      </div>
    </MemberLayout>
  );
}

// ── Member dashboard ───────────────────────────────────────────────────
function MemberHome({ nav, user }) {
  const { data: stats } = useFetch('/me/stats');
  const { data: circles } = useFetch('/me/memberships');
  const greeting = new Date().getHours() < 12 ? 'สวัสดีตอนเช้า' : new Date().getHours() < 18 ? 'สวัสดีตอนบ่าย' : 'สวัสดีตอนเย็น';
  const firstName = (user?.name || '').split(/\s+/)[0] || 'คุณ';

  return (
    <MemberLayout>
      <div style={{ padding: '14px 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Wordmark />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <NotificationBell />
          <div style={{ position: 'relative' }} className="ps-press" onClick={() => nav('/app/me')} data-testid="home-avatar">
            <Avatar name={user?.name} size={30} />
            <div style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', background: PS.ok, border: `2px solid ${PS.page}` }} />
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ fontSize: 13, color: PS.textMuted, marginBottom: 2 }}>{greeting},</div>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.3 }}>{firstName}</div>
        </div>

        <div className="ps-fade-up" style={{ margin: '0 16px 16px', background: PS.ink, color: '#f5f1ea', borderRadius: 22, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(245,241,234,0.6)' }}>วงแชร์ที่เข้าร่วม</div>
              <div className="num" style={{ fontSize: 38, fontWeight: 500, lineHeight: 1, marginTop: 6, letterSpacing: -0.5 }}>{stats?.circles_count ?? '—'} <span style={{ fontSize: 15, color: 'rgba(245,241,234,0.55)' }}>วง</span></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(245,241,234,0.6)' }}>งวดถัดไป</div>
              <div className="num" style={{ fontSize: 22, marginTop: 6 }}>{stats?.next_draw ?? '—'}</div>
            </div>
          </div>
          <div style={{ height: 1, background: 'rgba(245,241,234,0.12)', margin: '16px 0 14px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(245,241,234,0.55)' }}>Credit Score</div>
              <div className="num" style={{ fontSize: 28, lineHeight: 1, marginTop: 4 }}>{stats?.credit_score ?? user?.credit_score ?? '—'}</div>
            </div>
            <div style={{ flex: 1, margin: '0 16px' }}>
              <div style={{ height: 4, background: 'rgba(245,241,234,0.15)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, ((stats?.credit_score || 720) / 850) * 100)}%`, height: '100%', background: 'oklch(0.78 0.1 82)', transition: 'width 0.7s ease' }} />
              </div>
              <div style={{ fontSize: 10, color: 'rgba(245,241,234,0.55)', marginTop: 5, letterSpacing: 0.8, textTransform: 'uppercase' }}>ดีเยี่ยม · จ่ายตรง {stats?.on_time_pct ?? 100}%</div>
            </div>
          </div>
          <div style={{ position: 'absolute', right: -20, top: -20, width: 100, height: 100, border: '1px solid rgba(245,241,234,0.08)', transform: 'rotate(45deg)' }} />
        </div>

        <HighlightStrip nav={nav} />

        <ProductShareShowcase />

        <div style={{ padding: '0 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.2 }}>วงที่เข้าร่วม</div>
          <div className="ps-press" style={{ fontSize: 12, color: PS.textMuted }} onClick={() => nav('/app/discover')}>ค้นหาเพิ่ม</div>
        </div>

        {(circles || []).map((c) => (
          <ActiveCircleCard key={c.circle_id} c={c} onClick={() => nav(`/app/circle/${c.circle_id}`)} />
        ))}
        {circles && circles.length === 0 && (
          <div style={{ margin: '0 16px', padding: 24, textAlign: 'center', color: PS.textMuted, fontSize: 13, background: PS.surface, borderRadius: 18, border: `1px solid ${PS.line}` }}>
            ยังไม่ได้เข้าร่วมวงใด — <span style={{ color: PS.terracotta, fontWeight: 600 }} className="ps-press" onClick={() => nav('/app/discover')}>ค้นหาวงใกล้คุณ</span>
          </div>
        )}

        <div style={{ margin: '16px 16px 24px', padding: 12, display: 'flex', gap: 10, alignItems: 'center', background: 'rgba(32,28,24,0.03)', borderRadius: 12 }}>
          <Icon.shield size={18} stroke={PS.forest} />
          <div style={{ flex: 1, fontSize: 10.5, lineHeight: 1.4, color: PS.textMuted }}>
            วงแชร์ทุกวงปฏิบัติตาม <b style={{ color: PS.text }}>พ.ร.บ. การเล่นแชร์ พ.ศ. 2534</b> — ไม่เกิน 30 คน / ยอดรวมไม่เกิน 300,000 บาท ต่อวง
          </div>
        </div>
      </div>
    </MemberLayout>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { user, loading, login } = useAuth();
  if (loading) return <MemberLayout><div className="ps-live-dot" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: PS.textMuted }}>กำลังโหลด…</div></MemberLayout>;
  return user ? <MemberHome nav={nav} user={user} /> : <GuestHome nav={nav} login={login} />;
}
